import {useWindowSize} from '@react-hook/window-size';
import classNames from 'classnames';
import Head from 'next/head';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useBreakpoint} from '../../common/util/useBreakpoint';
import {Actions, useGameDebug} from '../debugSlice';
import Board from './Board';
import Debug from './Debug';

type Props = {};

const ASPECT_RATIO = 1920 / 1080; // Aspect ratio of background image
const MAX_WIDTH = 1920; // Width of background image (in px)
const FONT_SIZE = 10; // Font size corresponding to the above dimensions
const MOBILE_FONT_SIZE = '8px';

const Layout = ({}: Props): ReactElement => {
  const {isMobile} = useBreakpoint('mobile');
  const dispatch = useDispatch();
  const gameDebug = useGameDebug();

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [windowW, windowH] = useWindowSize();
  const [boardSize, setBoardSize] = useState<{width: number | string; height: number | string}>();

  // Whenever screen is resized, calculate board dimensions and font size
  useEffect(() => {
    dispatch(Actions.setWindowSize({width: windowW, height: windowH}));

    if (isMobile) {
      document.documentElement.style.fontSize = MOBILE_FONT_SIZE;
      setBoardSize({width: '100%', height: '100%'});
      return;
    }

    if (!windowW || !windowH || !containerRef.current) return;

    const {clientWidth: containerW, clientHeight: containerH, style} = containerRef.current;
    const newBoardSize = calculateBoardSize({width: containerW, height: containerH}, gameDebug.minBoardWidth);
    setBoardSize(newBoardSize);
    dispatch(Actions.setBoardSize(newBoardSize));

    const html = document.documentElement;
    html.style.fontSize = `${calculateFontSize(newBoardSize.width, gameDebug.fontZoom, gameDebug.resizeText)}px`;

    console.log('Window:', windowW, ' x ', windowH);
    console.log('Container:', containerW, ' x ', containerH);
    console.log('Board:', newBoardSize.width, ' x ', newBoardSize.height);
    console.log('fontSize:', document.documentElement.style.fontSize ?? 'undefined');

    return () => {
      // Clear HTML fontStyle when game is unloaded
      document.documentElement.style.fontSize = '';
    };
  }, [dispatch, gameDebug.fontZoom, gameDebug.minBoardWidth, gameDebug.resizeText, isMobile, windowH, windowW]);

  return (
    <div
      className={classNames(
        'absolute inset-1 flex items-start justify-start overflow-scroll m-[20px] mobile:m-0',
        '[&_:focus-visible]:outline-4 [&_:focus-visible]:outline-red-200   [&_:focus-visible]:outline-dashed'
      )}
      ref={scrollRef}
    >
      {/* Note: We can't use 'items-center justify-center' in the previous flex DIV due to this issue:
          https://stackoverflow.com/a/33455342/1077412
          Instead, the Board itself is centered by using 'm-auto' class on it.

          Board size is set dynamically whenever window is resized. This enforces a fixed aspect ratio.
       */}
      <Head>
        <title key={'title'}>Play Cropper! - Envirocrops</title>
      </Head>
      <div className={'flex w-full h-full'} ref={containerRef}>
        <Board style={boardSize} fullscreenElement={scrollRef} />
      </div>
      <Debug />
    </div>
  );
};

export default Layout;

function calculateBoardSize(containerSize: {width: number; height: number}, minBoardWidth: number) {
  let boardWidth = containerSize.width ?? 0;
  let boardHeight = boardWidth / ASPECT_RATIO;
  // If board height is larger than container's, adjust to fit
  if (boardHeight >= containerSize.height) {
    boardHeight = containerSize.height;
    boardWidth = boardHeight * ASPECT_RATIO;
  }
  // If board width is larger than max width, make it match max width
  if (boardWidth > MAX_WIDTH) {
    boardWidth = MAX_WIDTH;
    boardHeight = boardWidth / ASPECT_RATIO;
  }
  // If board width is smaller than min width, make it match min width
  if (boardWidth < minBoardWidth) {
    boardWidth = minBoardWidth;
    boardHeight = boardWidth / ASPECT_RATIO;
  }

  return {width: boardWidth, height: boardHeight};
}

function calculateFontSize(boardWidth: number, fontZoom: number, resize: boolean) {
  if (resize) {
    return (boardWidth * (FONT_SIZE + fontZoom)) / MAX_WIDTH;
  } else {
    return FONT_SIZE + fontZoom;
  }
}
